/**
 * Song Utilities - Beta Features
 * Inspired by Bopster's Yearify and cleanup tools
 */

// Patterns to remove from song titles
const TITLE_CLEANUP_PATTERNS = [
  // Video/Audio markers
  /\s*[-–—]\s*(Official\s*)?(Music\s*)?(Video|Audio|Clip|Lyric\s*Video|Lyrics?\s*Video|Visualizer|Visualiser)/gi,
  /\s*\(\s*(Official\s*)?(Music\s*)?(Video|Audio|Clip|Lyric\s*Video|Lyrics?\s*Video|Visualizer|Visualiser)\s*\)/gi,
  /\s*\[\s*(Official\s*)?(Music\s*)?(Video|Audio|Clip|Lyric\s*Video|Lyrics?\s*Video|Visualizer|Visualiser)\s*\]/gi,
  
  // Remaster markers
  /\s*[-–—]\s*\d{4}\s*(Remaster(ed)?|Re-?master(ed)?)/gi,
  /\s*\(\s*\d{4}\s*(Remaster(ed)?|Re-?master(ed)?)\s*\)/gi,
  /\s*\[\s*\d{4}\s*(Remaster(ed)?|Re-?master(ed)?)\s*\]/gi,
  /\s*[-–—]\s*(Remaster(ed)?|Re-?master(ed)?)\s*\d{4}/gi,
  /\s*\(\s*(Remaster(ed)?|Re-?master(ed)?)\s*\d{4}\s*\)/gi,
  /\s*\[\s*(Remaster(ed)?|Re-?master(ed)?)\s*\d{4}\s*\]/gi,
  /\s*[-–—]\s*(Remaster(ed)?|Re-?master(ed)?)/gi,
  /\s*\(\s*(Remaster(ed)?|Re-?master(ed)?)\s*\)/gi,
  /\s*\[\s*(Remaster(ed)?|Re-?master(ed)?)\s*\]/gi,
  
  // Digital Remaster
  /\s*[-–—]\s*(Digital(ly)?\s+)?(Remaster(ed)?)/gi,
  /\s*\(\s*(Digital(ly)?\s+)?(Remaster(ed)?)\s*\)/gi,
  
  // Anniversary editions
  /\s*[-–—]\s*\d+\s*(th|st|nd|rd)?\s*Anniversary\s*(Edition|Version)?/gi,
  /\s*\(\s*\d+\s*(th|st|nd|rd)?\s*Anniversary\s*(Edition|Version)?\s*\)/gi,
  
  // HD/HQ markers
  /\s*[-–—]\s*(HD|HQ|High\s*Quality|4K|1080p|720p)/gi,
  /\s*\(\s*(HD|HQ|High\s*Quality|4K|1080p|720p)\s*\)/gi,
  /\s*\[\s*(HD|HQ|High\s*Quality|4K|1080p|720p)\s*\]/gi,
  
  // Live markers (optional - sometimes you want to keep these)
  // /\s*\(\s*Live.*?\)/gi,
  
  // Bonus track markers
  /\s*[-–—]\s*(Bonus\s*Track)/gi,
  /\s*\(\s*(Bonus\s*Track)\s*\)/gi,
  
  // Version/Edit markers
  /\s*[-–—]\s*(Radio\s*Edit|Single\s*Version|Album\s*Version|Extended\s*Version|Extended\s*Mix|Original\s*Mix)/gi,
  /\s*\(\s*(Radio\s*Edit|Single\s*Version|Album\s*Version|Extended\s*Version|Extended\s*Mix|Original\s*Mix)\s*\)/gi,
  
  // From Album markers
  /\s*[-–—]\s*From\s*["'].*?["']/gi,
  /\s*\(\s*From\s*["'].*?["']\s*\)/gi,
  
  // Featured artists in title (move to artist field ideally)
  /\s*\(\s*feat\.?\s+.*?\)/gi,
  /\s*\[\s*feat\.?\s+.*?\]/gi,
  /\s*ft\.?\s+.+$/gi,
];

// Patterns to remove from artist names
const ARTIST_CLEANUP_PATTERNS = [
  // VEVO suffix
  /\s*VEVO$/gi,
  // Topic suffix (YouTube auto-generated)
  /\s*[-–—]\s*Topic$/gi,
  // Official suffix
  /\s*[-–—]\s*Official$/gi,
  /\s*\(\s*Official\s*\)/gi,
];

/**
 * Clean up a song title by removing common suffixes
 */
export const cleanTitle = (title: string): string => {
  let cleaned = title.trim();
  
  for (const pattern of TITLE_CLEANUP_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }
  
  // Clean up multiple spaces
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  // Remove trailing dashes or parentheses
  cleaned = cleaned.replace(/\s*[-–—]\s*$/, '').trim();
  cleaned = cleaned.replace(/\s*\(\s*\)\s*$/, '').trim();
  cleaned = cleaned.replace(/\s*\[\s*\]\s*$/, '').trim();
  
  return cleaned;
};

/**
 * Clean up an artist name
 */
export const cleanArtist = (artist: string): string => {
  let cleaned = artist.trim();
  
  for (const pattern of ARTIST_CLEANUP_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }
  
  // Clean up multiple spaces
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
};

/**
 * Clean both title and artist
 */
export const cleanSongInfo = (title: string, artist: string): { title: string; artist: string } => {
  return {
    title: cleanTitle(title),
    artist: cleanArtist(artist)
  };
};

/**
 * Calculate similarity between two strings (case insensitive)
 * Returns a value between 0 and 1
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();
  
  if (s1 === s2) return 1;
  if (s1.length === 0 || s2.length === 0) return 0;
  
  // Levenshtein distance
  const matrix: number[][] = [];
  
  for (let i = 0; i <= s1.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= s2.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= s1.length; i++) {
    for (let j = 1; j <= s2.length; j++) {
      if (s1[i - 1] === s2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  const maxLen = Math.max(s1.length, s2.length);
  return 1 - (matrix[s1.length][s2.length] / maxLen);
};

export interface DuplicateResult {
  songId: string;
  duplicateOf: string;
  similarity: number;
  type: 'exact' | 'similar';
}

export interface SongForDuplicateCheck {
  id: string;
  title: string;
  artist: string;
  year?: number;
}

/**
 * Find duplicate songs in a list
 * Returns array of duplicate pairs
 */
export const findDuplicates = (
  songs: SongForDuplicateCheck[],
  similarityThreshold: number = 0.85
): DuplicateResult[] => {
  const duplicates: DuplicateResult[] = [];
  const checked = new Set<string>();
  
  for (let i = 0; i < songs.length; i++) {
    const song1 = songs[i];
    const key1 = `${song1.title.toLowerCase()}-${song1.artist.toLowerCase()}`;
    
    if (checked.has(song1.id)) continue;
    
    for (let j = i + 1; j < songs.length; j++) {
      const song2 = songs[j];
      
      if (checked.has(song2.id)) continue;
      
      // Check for exact match (after cleaning)
      const clean1 = cleanSongInfo(song1.title, song1.artist);
      const clean2 = cleanSongInfo(song2.title, song2.artist);
      
      if (
        clean1.title.toLowerCase() === clean2.title.toLowerCase() &&
        clean1.artist.toLowerCase() === clean2.artist.toLowerCase()
      ) {
        duplicates.push({
          songId: song2.id,
          duplicateOf: song1.id,
          similarity: 1,
          type: 'exact'
        });
        checked.add(song2.id);
        continue;
      }
      
      // Check for similar match
      const titleSim = calculateSimilarity(clean1.title, clean2.title);
      const artistSim = calculateSimilarity(clean1.artist, clean2.artist);
      const avgSim = (titleSim + artistSim) / 2;
      
      if (titleSim >= similarityThreshold && artistSim >= similarityThreshold) {
        duplicates.push({
          songId: song2.id,
          duplicateOf: song1.id,
          similarity: avgSim,
          type: 'similar'
        });
        // Don't mark as checked - might be duplicate of multiple
      }
    }
  }
  
  return duplicates;
};

/**
 * Preview what cleanup would do to a song
 */
export const previewCleanup = (title: string, artist: string): {
  originalTitle: string;
  originalArtist: string;
  cleanedTitle: string;
  cleanedArtist: string;
  titleChanged: boolean;
  artistChanged: boolean;
} => {
  const cleaned = cleanSongInfo(title, artist);
  
  return {
    originalTitle: title,
    originalArtist: artist,
    cleanedTitle: cleaned.title,
    cleanedArtist: cleaned.artist,
    titleChanged: title !== cleaned.title,
    artistChanged: artist !== cleaned.artist
  };
};
